<script setup lang="ts">
import useEmblaCarousel from 'embla-carousel-vue'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import type { IntRange } from 'type-fest'

const { t, tm, rt } = useI18n()

const [emblaRef, emblaApi] = useEmblaCarousel({
  align: 'start',
  slidesToScroll: 'auto',
  loop: false,
}, [
  WheelGesturesPlugin(),
])

const { selectedIndex, scrollSnaps, handleClick } = useEmblaDots(emblaApi)
const { isPrevDisabled, isNextDisabled, handlePrevious, handleNext }
  = useEmblaButtons(emblaApi)

function getData(index: StringifiedInt<IntRange<0, 9>>) {
  const baseUrl = `${S3_ASSETS_URL}/home/success-stories`

  return match(index)
    .with('0', () => ({
      featured_image: `${baseUrl}/fraunces-tavern/featured.jpg`,
      logo_image: `${baseUrl}/fraunces-tavern/logo.png`,
    }))
    .with('1', () => ({
      featured_image: `${baseUrl}/mezza-luna/featured.jpg`,
      logo_image: `${baseUrl}/mezza-luna/logo.png`,
    }))
    .with('2', () => ({
      featured_image: `${baseUrl}/props/featured.jpg`,
      logo_image: `${baseUrl}/props/logo.png`,
    }))
    .with('3', () => ({
      featured_image: `${baseUrl}/gaos-bbq/featured.jpg`,
      logo_image: `${baseUrl}/gaos-bbq/logo.png`,
    }))
    .with('4', () => ({
      featured_image: `${baseUrl}/carmelas-bistro/featured.jpg`,
      logo_image: `${baseUrl}/carmelas-bistro/logo.png`,
    }))
    .with('5', () => ({
      featured_image: `${baseUrl}/juke-bar/featured.jpg`,
      logo_image: `${baseUrl}/juke-bar/logo.png`,
    }))
    .with('6', () => ({
      featured_image: `${baseUrl}/five-church/featured.jpg`,
      logo_image: `${baseUrl}/five-church/logo.png`,
    }))
    .with('7', () => ({
      featured_image: `${baseUrl}/docks-off-5th/featured.jpg`,
      logo_image: `${baseUrl}/docks-off-5th/logo.png`,
    }))
    .with('8', () => ({
      featured_image: `${baseUrl}/fat-boys-barbecue/featured.jpg`,
      logo_image: `${baseUrl}/fat-boys-barbecue/logo.png`,
    }))
    .exhaustive()
}
</script>

<template>
  <div class="bg-white">
    <section
      class="mx-auto max-w-screen-xl overflow-x-clip px-4 py-8 lg:px-8 lg:pt-16"
    >
      <UiSectionHeadline
        :title="t('home.section.success_stories.title')"
        :subtitle="t('home.section.success_stories.subtitle')"
      />

      <div class="mt-12 flex flex-col gap-y-4 lg:gap-y-8">
        <div
          ref="emblaRef"
          class="relative select-none"
        >
          <div
            class="flex gap-x-2.5 [backface-visibility:hidden] lg:gap-x-5"
          >
            <div
              v-for="(item, itemIndex) in tm(
                'home.section.success_stories.items',
              )"
              :key="itemIndex"
              class="relative flex shrink-0 grow-0 basis-[calc(100%-1.25rem*2)] justify-stretch md:basis-[calc((100%-1.25rem*2)/2)] lg:basis-[calc((100%-1.25rem*2)/3)]"
            >
              <div
                class="flex w-full cursor-move flex-col overflow-clip rounded-2xl border border-gray-200 bg-[#F8F8F0]"
              >
                <div
                  class="relative flex h-64 min-w-0 overflow-clip rounded-b-2xl"
                >
                  <UiImageWithOverlay
                    :src="getData(itemIndex).featured_image"
                    :alt="`${rt(item.author)} Featured Image`"
                    container-class="!absolute inset-0"
                    class="aspect-video size-full object-cover"
                  />

                  <div
                    v-if="getData(itemIndex).logo_image"
                    class="absolute inset-0 z-[2] grid place-content-center"
                  >
                    <img
                      :src="
                        getData(itemIndex).logo_image!
                      "
                      :alt="`${rt(item.author)} Logo`"
                      class="max-h-32 max-w-48 object-contain xl:max-h-40 xl:max-w-56"
                    >
                  </div>

                  <div
                    class="relative z-10 grid place-content-end p-5"
                  >
                    <span
                      class="flex text-lg font-medium text-white"
                    >
                      {{ rt(item.author) }}
                    </span>

                    <span class="flex text-white/80">
                      {{ rt(item.location) }}
                    </span>
                  </div>
                </div>

                <div class="grid place-items-start gap-y-1 p-5">
                  <div class="flex w-full items-center justify-between">
                    <h4
                      class="text-3xl font-bold text-orange-500"
                    >
                      {{ rt(item.growth_value) }}
                    </h4>

                    <time
                      class="inline-flex rounded-full bg-gray-700/5 px-3 py-1 text-sm"
                      :value="rt(item.date)"
                    >
                      {{ format(rt(item.date), "MMMM yyyy") }}
                    </time>
                  </div>

                  <p class="text-lg">
                    {{ rt(item.growth_described_as) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <nav class="flex items-center justify-between gap-x-5">
          <div class="hidden gap-x-2 sm:flex">
            <button
              v-for="(_, index) in scrollSnaps"
              :key="index"
              type="button"
              :class="[
                'size-3 rounded-[50%]',
                selectedIndex === index
                  ? 'bg-orange-500'
                  : 'bg-gray-200',
              ]"
              @click="handleClick(index)"
            />
          </div>

          <div class="ml-auto flex gap-x-2">
            <button
              type="button"
              class="inline-flex size-12 items-center justify-center rounded-lg border transition-all enabled:border-neutral-100 enabled:bg-white enabled:shadow-2xl disabled:border-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
              :disabled="isPrevDisabled"
              @click="handlePrevious"
            >
              <UiIcon
                name="arrow-forward-rounded"
                class="-rotate-180"
              />
            </button>

            <button
              type="button"
              class="inline-flex size-12 items-center justify-center rounded-lg border transition-all enabled:border-neutral-100 enabled:bg-white enabled:shadow-2xl disabled:border-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
              :disabled="isNextDisabled"
              @click="handleNext"
            >
              <UiIcon name="arrow-forward-rounded" />
            </button>
          </div>
        </nav>
      </div>
    </section>
  </div>
</template>
